import { env } from '@/env'
import { resolveApiUrl } from '@/shared/lib/api-url'

export type LegalDocumentSummary = {
  id: string
  version: string
  sha256: string
  url: string
}

export type LegalAccessState =
  | { mode: 'unavailable' }
  | ({ mode: 'accepted' } & LegalDocumentSet)
  | ({ mode: 'pending' } & LegalDocumentSet)

type LegalDocumentSet = {
  accountType: 'personal' | 'member'
  offer: LegalDocumentSummary
  aup: LegalDocumentSummary
  privacy: LegalDocumentSummary
}

export async function getLegalAccessState(): Promise<LegalAccessState | null> {
  const response = await fetch(
    resolveApiUrl(env.VITE_API_URL, '/legal/access-state'),
    { credentials: 'include' },
  )
  if (response.status === 401) return null
  if (!response.ok) {
    throw new Error(`Unable to check legal access (${response.status}).`)
  }

  return (await response.json()) as LegalAccessState
}

export async function acceptLegalOffer(
  state: Extract<LegalAccessState, { mode: 'pending' | 'accepted' }>,
): Promise<void> {
  const member = state.accountType === 'member'
  const response = await fetch(
    resolveApiUrl(
      env.VITE_API_URL,
      member ? '/legal/accept-member-terms' : '/legal/accept-current-offer',
    ),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        'idempotency-key': crypto.randomUUID(),
      },
      body: JSON.stringify({
        ...(member
          ? { memberTermsVersionId: state.offer.id }
          : { offerVersionId: state.offer.id }),
        aupVersionId: state.aup.id,
        privacyVersionId: state.privacy.id,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(`Unable to record acceptance (${response.status}).`)
  }
}

export async function declineLegalTerms(
  state: Extract<LegalAccessState, { mode: 'pending' }>,
): Promise<void> {
  const response = await fetch(
    resolveApiUrl(env.VITE_API_URL, '/legal/events'),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        'idempotency-key': crypto.randomUUID(),
      },
      body: JSON.stringify({
        documentKey: state.accountType === 'member' ? 'member_terms' : 'offer',
        expectedVersionId: state.offer.id,
        eventType: 'declined',
      }),
    },
  )
  if (!response.ok)
    throw new Error(`Unable to record decline (${response.status}).`)
}
